import { useTranslation } from "react-i18next";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "yet-another-react-lightbox/plugins/captions.css";
import Box from "@mui/material/Box";
import ClearIcon from "@mui/icons-material/Clear";
import {
  Container,
  FormHelperText,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import * as React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { usePatchProject } from "../../api/use-project";

export const ProjectContributors = ({ project, reloadProject }) => {
  const { t } = useTranslation(["project", "common", "validation"]);
  const contributors = project.contributors ?? [];

  const schema = yup.object({
    email: yup
      .string()
      .emailValidatorJs(t("emailValidation", { field: t("email") }))
      .required(t("requiredValidation", { field: t("email") })),
  });
  const dv = {
    email: "",
  };

  const {
    handleSubmit,
    formState: { errors, isDirty },
    control,
    reset,
  } = useForm({
    defaultValues: dv,
    resolver: yupResolver(schema),
  });

  const [{ loading, error }, execute] = usePatchProject(project?.id);
  const errorMessage =
    error &&
    (error.response?.data?.error_message ||
      t("genericError", { action: t("saveContributors") }));

  const onSubmit = async (data) => {
    await execute({
      data: {
        contributors: [...contributors, data.email],
      },
    });
    reset();
    reloadProject();
  };

  const handleRemove = async (email) => {
    await execute({
      data: {
        contributors: contributors
          .filter((x) => x !== email),
      },
    });
    reloadProject();
  };

  return (
    <>
      <Container maxWidth="sm">
        {contributors.length > 0 && (
          <List sx={{ bgcolor: "background.paper" }}>
            {contributors.map((value) => (
              <ListItem
                key={value}
                secondaryAction={
                  <IconButton
                    aria-label={t("remove")}
                    onClick={() => handleRemove(value)}
                  >
                    <ClearIcon />
                  </IconButton>
                }
              >
                <ListItemText primary={value} />
              </ListItem>
            ))}
          </List>
        )}
        {contributors.length === 0 && t("noContributors")}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            mt: 4,
            "& > .MuiFormControl-root": {
              mb: 3,
            },
          }}
          component="form"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <Controller
            render={({ field }) => (
              <TextField
                fullWidth
                error={!!errors.email}
                helperText={errors.email?.message}
                label={t("email")}
                autoComplete="new-password"
                {...field}
              />
            )}
            control={control}
            name="email"
          />
          {errorMessage && (
            <FormHelperText error>{errorMessage}</FormHelperText>
          )}
          <LoadingButton
            type="submit"
            loading={loading}
            disabled={!isDirty}
            variant="contained"
          >
            {t("add")}
          </LoadingButton>
        </Box>
      </Container>
    </>
  );
};
